import type { NavigatorWithConnection } from "@/lib/browserCapabilities";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Upload,
  CheckCircle,
  AlertCircle,
  Loader2,
  RotateCcw,
  Wifi,
  WifiOff,
  Zap,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";

interface UploadProgressProps {
  current: number;
  total: number;
  currentPhoto: string;
  status: "preparing" | "uploading" | "registering" | "completed" | "error";
  className?: string;
  failedPhotos?: string[];
  retryCount?: number;
  onRetryFailedPhotos?: () => void;
  onContinue?: () => void;
  // Enhanced parallel upload tracking
  individualProgress?: Record<
    string,
    {
      status: "pending" | "uploading" | "completed" | "failed";
      progress: number;
    }
  >;
  uploadMode?: "sequential" | "parallel";
  // Performance tracking
  uploadSpeed?: number; // MB/s
  estimatedTimeRemaining?: number; // seconds
  totalSize?: number; // MB
  uploadedSize?: number; // MB
}

export function UploadProgress({
  current,
  total,
  currentPhoto,
  status,
  className,
  failedPhotos = [],
  retryCount = 0,
  onRetryFailedPhotos,
  onContinue,
  individualProgress = {},
  uploadMode = "sequential",
  uploadSpeed = 0,
  estimatedTimeRemaining = 0,
  totalSize = 0,
  uploadedSize = 0,
}: UploadProgressProps) {
  const [networkQuality, setNetworkQuality] = useState<
    "fast" | "slow" | "offline"
  >("fast");
  const [showDetailedStats, setShowDetailedStats] = useState(false);
  const progress = total > 0 ? (current / total) * 100 : 0;

  // Monitor network quality
  useEffect(() => {
    const updateNetworkQuality = () => {
      if (!navigator.onLine) {
        setNetworkQuality("offline");
        return;
      }

      // Use connection API if available
      const connection =
        (navigator as NavigatorWithConnection).connection ||
        (navigator as NavigatorWithConnection).mozConnection ||
        (navigator as NavigatorWithConnection).webkitConnection;
      if (connection) {
        const effectiveType = connection.effectiveType;
        if (effectiveType === "slow-2g" || effectiveType === "2g") {
          setNetworkQuality("slow");
        } else {
          setNetworkQuality("fast");
        }
      }
    };

    updateNetworkQuality();
    window.addEventListener("online", updateNetworkQuality);
    window.addEventListener("offline", updateNetworkQuality);

    return () => {
      window.removeEventListener("online", updateNetworkQuality);
      window.removeEventListener("offline", updateNetworkQuality);
    };
  }, []);

  const getStatusIcon = () => {
    switch (status) {
      case "preparing":
        return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
      case "uploading":
        return <Upload className="h-4 w-4 animate-pulse text-blue-500" />;
      case "registering":
        return <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />;
      case "completed":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "error":
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Upload className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatTimeRemaining = (seconds: number) => {
    if (seconds < 60) return `${Math.ceil(seconds)}s remaining`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${Math.ceil(remainingSeconds)}s remaining`;
  };

  const formatSize = (mb: number) => {
    if (mb < 1) return `${Math.round(mb * 1024)}KB`;
    return `${mb.toFixed(1)}MB`;
  };

  const getStatusText = () => {
    switch (status) {
      case "preparing":
        return networkQuality === "offline"
          ? "Offline - photos will upload when connection returns"
          : networkQuality === "slow"
            ? "Preparing photos for upload (optimizing for slow connection)..."
            : "Preparing photos for upload...";
      case "uploading":
        if (
          uploadMode === "parallel" &&
          Object.keys(individualProgress).length > 0
        ) {
          const completed = Object.values(individualProgress).filter(
            (p) => p.status === "completed",
          ).length;
          const failed = Object.values(individualProgress).filter(
            (p) => p.status === "failed",
          ).length;
          const uploading = Object.values(individualProgress).filter(
            (p) => p.status === "uploading",
          ).length;
          let baseText = `Uploading all photos simultaneously... (${completed + failed}/${total} completed)`;

          if (uploading > 0) {
            baseText += ` • ${uploading} in progress`;
          }

          // Add speed and time info if available
          if (uploadSpeed > 0 && estimatedTimeRemaining > 0) {
            baseText += ` • ${uploadSpeed.toFixed(1)} MB/s • ${formatTimeRemaining(estimatedTimeRemaining)}`;
          }
          return baseText;
        } else if (currentPhoto && currentPhoto.includes("Completed")) {
          return currentPhoto;
        } else if (
          currentPhoto &&
          (currentPhoto.includes("Failed") || currentPhoto.includes("Error"))
        ) {
          return currentPhoto;
        } else {
          let baseText = `Uploading ${currentPhoto || "photo"}...`;
          if (uploadSpeed > 0) {
            baseText += ` (${uploadSpeed.toFixed(1)} MB/s)`;
          }
          return baseText;
        }
      case "registering":
        return "Finalizing uploads... generating secure links";
      case "completed":
        if (failedPhotos.length > 0) {
          return `${failedPhotos.length} photos failed to upload`;
        }
        return "All photos uploaded successfully!";
      case "error":
        return networkQuality === "offline"
          ? "Upload paused - check your internet connection"
          : "Upload process failed. Please try again.";
      default:
        return "Uploading photos...";
    }
  };

  return (
    <Card className={cn("w-full max-w-md mx-auto", className)}>
      <CardContent className="p-4">
        {/* Network Status Indicator */}
        {networkQuality !== "fast" && (
          <div
            className={cn(
              "flex items-center gap-2 p-2 rounded-lg mb-3 text-xs font-medium",
              networkQuality === "offline"
                ? "bg-red-50 text-red-700 border border-red-200"
                : networkQuality === "slow"
                  ? "bg-yellow-50 text-yellow-700 border border-yellow-200"
                  : "",
            )}
          >
            {networkQuality === "offline" ? (
              <WifiOff className="w-4 h-4" />
            ) : (
              <Wifi className="w-4 h-4" />
            )}
            {networkQuality === "offline"
              ? "No internet connection"
              : "Slow connection detected"}
          </div>
        )}

        <div className="flex items-center space-x-3 mb-3">
          {getStatusIcon()}
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-900">
                {getStatusText()}
              </p>
              {(uploadSpeed > 0 || totalSize > 0) && (
                <button
                  onClick={() => setShowDetailedStats(!showDetailedStats)}
                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <Zap className="w-3 h-3" />
                  Stats
                </button>
              )}
            </div>
            {status === "uploading" && (
              <p className="text-xs text-gray-500">
                {current} of {total} photos
                {retryCount > 0 && (
                  <span className="ml-2 text-orange-600">
                    (Retry {retryCount}/3)
                  </span>
                )}
                {totalSize > 0 && uploadedSize > 0 && (
                  <span className="ml-2 text-blue-600">
                    {formatSize(uploadedSize)} / {formatSize(totalSize)}
                  </span>
                )}
              </p>
            )}
          </div>
        </div>

        {/* Detailed Stats Panel */}
        {showDetailedStats && (uploadSpeed > 0 || totalSize > 0) && (
          <div className="bg-gray-50 rounded-lg p-3 mb-3 text-xs space-y-2">
            <div className="grid grid-cols-2 gap-4">
              {uploadSpeed > 0 && (
                <div>
                  <span className="text-gray-600">Speed:</span>
                  <span className="ml-2 font-medium">
                    {uploadSpeed.toFixed(1)} MB/s
                  </span>
                </div>
              )}
              {estimatedTimeRemaining > 0 && (
                <div>
                  <span className="text-gray-600">ETA:</span>
                  <span className="ml-2 font-medium">
                    {formatTimeRemaining(estimatedTimeRemaining)}
                  </span>
                </div>
              )}
              {totalSize > 0 && (
                <div>
                  <span className="text-gray-600">Total Size:</span>
                  <span className="ml-2 font-medium">
                    {formatSize(totalSize)}
                  </span>
                </div>
              )}
              <div>
                <span className="text-gray-600">Network:</span>
                <span
                  className={cn(
                    "ml-2 font-medium",
                    networkQuality === "fast"
                      ? "text-green-600"
                      : networkQuality === "slow"
                        ? "text-yellow-600"
                        : "text-red-600",
                  )}
                >
                  {networkQuality === "fast"
                    ? "Fast"
                    : networkQuality === "slow"
                      ? "Slow"
                      : "Offline"}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Progress value={progress} className="h-2" />
          <div className="flex justify-between text-xs text-gray-500">
            <span>{Math.round(progress)}%</span>
            <span>
              {current}/{total}
            </span>
          </div>
        </div>

        {/* Individual Photo Progress (for parallel uploads) */}
        {uploadMode === "parallel" &&
          Object.keys(individualProgress).length > 0 &&
          status === "uploading" && (
            <div className="mt-4">
              <p className="text-xs font-medium text-gray-600 mb-2">
                Photo Upload Status:
              </p>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(individualProgress).map(
                  ([photoId, progress]) => {
                    const getPhotoIcon = () => {
                      switch (progress.status) {
                        case "pending":
                          return (
                            <div className="w-2 h-2 bg-gray-300 rounded-full" />
                          );
                        case "uploading":
                          return (
                            <Loader2 className="w-3 h-3 animate-spin text-blue-500" />
                          );
                        case "completed":
                          return (
                            <CheckCircle className="w-3 h-3 text-green-500" />
                          );
                        case "failed":
                          return (
                            <AlertCircle className="w-3 h-3 text-red-500" />
                          );
                        default:
                          return (
                            <div className="w-2 h-2 bg-gray-300 rounded-full" />
                          );
                      }
                    };

                    const getPhotoTitle = () => {
                      switch (photoId) {
                        case "upper_occlusal":
                          return "Upper";
                        case "lower_occlusal":
                          return "Lower";
                        case "frontal_retracted_apart":
                          return "Front Open";
                        case "frontal_in_occlusion":
                          return "Front Closed";
                        case "right_buccal":
                          return "Right Side";
                        case "left_buccal":
                          return "Left Side";
                        default:
                          return photoId;
                      }
                    };

                    return (
                      <div
                        key={photoId}
                        className="flex items-center gap-2 p-2 bg-gray-50 rounded text-xs"
                      >
                        {getPhotoIcon()}
                        <span
                          className={cn(
                            "flex-1 truncate",
                            progress.status === "completed" && "text-green-700",
                            progress.status === "failed" && "text-red-700",
                            progress.status === "uploading" && "text-blue-700",
                          )}
                        >
                          {getPhotoTitle()}
                        </span>
                      </div>
                    );
                  },
                )}
              </div>
              <div className="mt-2 text-xs text-gray-500 text-center">
                All photos are being uploaded at the same time for faster
                processing
              </div>
            </div>
          )}

        {/* Retry prompt for failed uploads */}
        {status === "completed" && failedPhotos.length > 0 && (
          <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-orange-900 mb-1">
                  {failedPhotos.length} photo
                  {failedPhotos.length > 1 ? "s" : ""} failed to upload
                </h4>
                <p className="text-xs text-orange-700 mb-3">
                  The following photos couldn't be uploaded:{" "}
                  {failedPhotos.join(", ")}
                </p>
                <div className="flex gap-2">
                  {onRetryFailedPhotos && (
                    <Button
                      onClick={onRetryFailedPhotos}
                      size="sm"
                      variant="outline"
                      className="text-orange-700 border-orange-300 hover:bg-orange-100"
                    >
                      <RotateCcw className="w-3 h-3 mr-1" />
                      Retry Failed Photos
                    </Button>
                  )}
                  {onContinue && (
                    <Button
                      onClick={onContinue}
                      size="sm"
                      variant="outline"
                      className="text-gray-700 border-gray-300 hover:bg-gray-100"
                    >
                      Continue Anyway
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
